<script setup lang="ts">
import { computed } from 'vue';
import type {
  FlexDirection,
  ResponseSize,
  JustifyContent,
  AlignItems,
} from './types';
import { isString, isNull } from 'lodash';

const DEFAULT_FLEX_DIRECTION = 'row';
const DEFAULT_CONTENT_JUSTIFICATION = 'start';
const DEFAULT_ALIGN_ITEMS = 'stretch';
const DEFAULT_ALIGN_CONTENT = 'start';
const DEFAULT_WRAP_ITEMS = false;

const props = withDefaults(
  defineProps<{
    flex?: boolean;
    direction?: FlexDirection;
    inline?: boolean;
    reverseDirection?: boolean;
    responsive?: ResponseSize | null;
    justifyContent?: JustifyContent;
    alignItems?: AlignItems;
    alignContent?: JustifyContent;
    wrapItems?: boolean;
    reverseWrapItems?: boolean;
    spacing?: string | null;
    gap?: string | number | null;
    rowGap?: string | number | null;
    columnGap?: string | number | null;
  }>(),
  {
    flex: true,
    direction: 'row',
    inline: false,
    reverseDirection: false,
    responsive: null,
    justifyContent: 'start',
    alignItems: 'stretch',
    alignContent: 'start',
    wrapItems: false,
    reverseWrapItems: false,
    spacing: null,
    gap: null,
    rowGap: null,
    columnGap: null,
  },
);

const getFlex = () => {
  if (!props.flex) {
    return;
  }
  return ['d', props.responsive, props.inline ? 'inline' : null, 'flex']
    .filter((p) => !!p)
    .join('-');
};

const getDirection = () => {
  if (
    !props.flex ||
    (!props.responsive &&
      !props.reverseDirection &&
      props.direction === DEFAULT_FLEX_DIRECTION)
  ) {
    return;
  }
  return [
    'flex',
    props.responsive,
    props.direction,
    props.reverseDirection ? 'reverse' : null,
  ]
    .filter((p) => !!p)
    .join('-');
};

const getContentJustification = () => {
  if (
    !props.flex ||
    (!props.responsive &&
      props.justifyContent === DEFAULT_CONTENT_JUSTIFICATION)
  ) {
    return;
  }
  return ['justify-content', props.responsive, props.justifyContent]
    .filter((p) => !!p)
    .join('-');
};

const getItemsAlignment = () => {
  if (
    !props.flex ||
    (!props.responsive && props.alignItems === DEFAULT_ALIGN_ITEMS)
  ) {
    return;
  }
  return ['align-items', props.responsive, props.alignItems]
    .filter((p) => !!p)
    .join('-');
};

const getContentAlignment = () => {
  if (
    !props.flex ||
    (!props.responsive && props.alignContent === DEFAULT_ALIGN_CONTENT)
  ) {
    return;
  }
  return ['align-content', props.responsive, props.alignContent]
    .filter((p) => !!p)
    .join('-');
};

const getItemsWrapping = () => {
  if (
    !props.flex ||
    (!props.responsive &&
      !props.reverseWrapItems &&
      props.wrapItems === DEFAULT_WRAP_ITEMS)
  ) {
    return;
  }
  return [
    'flex',
    props.responsive,
    props.wrapItems ? 'wrap' : 'nowrap',
    props.reverseWrapItems ? 'reverse' : null,
  ]
    .filter((p) => !!p)
    .join('-');
};

const getSpacing = () => {
  return props.spacing || undefined;
};

const getGaps = () => {
  const result = [];

  if (!isNull(props.gap)) {
    result.push(`gap-${props.gap}`);
  }

  if (!isNull(props.rowGap)) {
    result.push(`row-gap-${props.rowGap}`);
  }

  if (!isNull(props.columnGap)) {
    result.push(`column-gap-${props.columnGap}`);
  }

  if (result) {
    return result.join(' ');
  }
};

const className = computed(() => {
  return [
    'itp-box',
    getFlex(),
    getDirection(),
    getContentJustification(),
    getContentAlignment(),
    getItemsAlignment(),
    getItemsWrapping(),
    getSpacing(),
    getGaps(),
  ].filter((p) => !!p);
});
</script>

<template>
  <div :class="className">
    <slot />
  </div>
</template>
